import { Button, Kind, Size } from '@thanx/uikit/button'
import { getBaseStyle, getSizeStyle } from '@thanx/uikit/button/style'
import { Modal } from '@thanx/uikit/modal'
import { getDialogStyle } from '@thanx/uikit/modal/style'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import type { LiveUpdatesDisableErrorKind } from 'api/liveUpdates.types'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import { merchantTheme } from 'theme'
import { borderColors, solidBorder } from './styleHelpers'
import TurnOffIllustration from './TurnOffIllustration'

const t = buildTranslate('live_updates.turn_off_modal')

const MODAL_Z_INDEX = 1200
const CONTENT_MAX_WIDTH = '880px'
const CARD_MIN_WIDTH = '200px'
const DANGER_BACKGROUND = palette.cayenneFill
const DANGER_HOVER_BACKGROUND = palette.cayenneHover
const DANGER_ACTIVE_BACKGROUND = palette.cayenneActive

type DangerButtonStyleArgs = Parameters<typeof getBaseStyle>[0]

// @thanx/uikit 4.0.0 has no danger button kind. `overrides.BaseButton` replaces
// uikit's own style slot, so the base and size styles are spread back in and
// only the colors are ours.
const dangerButtonOverrides = {
  BaseButton: {
    style: (styleArgs: DangerButtonStyleArgs) => ({
      ...getBaseStyle(styleArgs),
      ...getSizeStyle(Size.STANDARD),
      color: palette.white,
      backgroundColor: DANGER_BACKGROUND,
      ...borderColors(DANGER_BACKGROUND),
      ':hover': {
        backgroundColor: DANGER_HOVER_BACKGROUND,
        ...borderColors(DANGER_HOVER_BACKGROUND),
      },
      // uikit sets `:active:enabled`; without ours, pressing shows the primary
      // color.
      ':active:enabled': {
        backgroundColor: DANGER_ACTIVE_BACKGROUND,
        ...borderColors(DANGER_ACTIVE_BACKGROUND),
      },
      ':disabled': {
        backgroundColor: DANGER_BACKGROUND,
        ...borderColors(DANGER_BACKGROUND),
        opacity: 0.6,
      },
    }),
  },
}

const CONSEQUENCES = [
  { key: 'lock_screen', testId: 'lu-turn-off-lock-screen' },
  { key: 'in_app', testId: 'lu-turn-off-in-app' },
  { key: 'feedback', testId: 'lu-turn-off-feedback' },
] as const

export interface TurnOffConfirmModalProps {
  isOpen: boolean
  isTurningOff: boolean
  errorKind: LiveUpdatesDisableErrorKind | null
  onConfirm: () => void
  onCancel: () => void
}

const TurnOffConfirmModal = ({
  isOpen,
  isTurningOff,
  errorKind,
  onConfirm,
  onCancel,
}: TurnOffConfirmModalProps) => {
  const [css] = useStyletron()

  return (
    <Modal
      isOpen={isOpen}
      onClose={isTurningOff ? undefined : onCancel}
      size="auto"
      closeType="small"
      closeable={!isTurningOff}
      zIndex={MODAL_Z_INDEX}
      returnFocus={false}
      animate={false}
      // Baseweb hardcodes aria-label="dialog" on the Dialog. Overriding the
      // Dialog slot replaces uikit's, so its style is passed back in.
      overrides={{
        Dialog: {
          props: { 'aria-label': t('title') },
          style: getDialogStyle('auto'),
        },
      }}
    >
      <div
        data-testid="lu-turn-off-modal"
        className={`p-xl d-flex flex-column ${css({
          maxWidth: CONTENT_MAX_WIDTH,
        })}`}
      >
        <Text.Header3 tag="h3" className="m-0">
          {t('title')}
        </Text.Header3>
        <Text.BodyText3 color="grey70" className="mt-xs">
          {t('body')}
        </Text.BodyText3>

        <Text.Header4
          tag="h4"
          color={merchantTheme.colors.cayenne70}
          className="mt-l mb-s"
        >
          {t('danger_zone')}
        </Text.Header4>
        <div
          data-testid="lu-turn-off-danger-zone"
          className={`p-m ${css({
            display: 'flex',
            flexWrap: 'wrap',
            gap: '16px',
            ...solidBorder(palette.cayenne),
            borderTopLeftRadius: '8px',
            borderTopRightRadius: '8px',
            borderBottomRightRadius: '8px',
            borderBottomLeftRadius: '8px',
            backgroundColor: palette.white,
          })}`}
        >
          {CONSEQUENCES.map(({ key, testId }) => (
            <div
              key={key}
              data-testid={testId}
              className={`p-m ${css({
                flexGrow: 1,
                flexBasis: CARD_MIN_WIDTH,
                minWidth: CARD_MIN_WIDTH,
                ...solidBorder(palette.grey20),
                borderTopLeftRadius: '8px',
                borderTopRightRadius: '8px',
                borderBottomRightRadius: '8px',
                borderBottomLeftRadius: '8px',
                backgroundColor: palette.grey05,
              })}`}
            >
              <TurnOffIllustration kind={key} />
              <Text.Header4 tag="h5" className="mt-s mb-0">
                {t(`${key}_title`)}
              </Text.Header4>
              <Text.BodyText4 color="grey70" className="mt-xs">
                {t(`${key}_body`)}
              </Text.BodyText4>
            </div>
          ))}
        </div>

        {errorKind && (
          <div role="alert" className="mt-s">
            <Text.BodyText4 color={merchantTheme.colors.cayenne70}>
              {t(
                errorKind === 'recovery_ai_turn_off_failed'
                  ? 'error_recovery_ai'
                  : 'error'
              )}
            </Text.BodyText4>
          </div>
        )}

        <div className="d-flex justify-content-end mt-l">
          <Button
            kind={Kind.SECONDARY}
            className="mr-m"
            disabled={isTurningOff}
            onClick={onCancel}
          >
            {t('cancel')}
          </Button>
          <Button
            overrides={dangerButtonOverrides}
            disabled={isTurningOff}
            isLoading={isTurningOff}
            onClick={onConfirm}
          >
            {t('confirm')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default TurnOffConfirmModal
